import { Box, Drawer, IconButton, Stack, useTheme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { Logo } from "../logo/Logo";
import { LogoEye } from "../logo/LogoEye";
import { DrawerNavMenu } from "./DrawerNavMenu";
import { DrawerThemeToggle } from "./DrawerThemeToggle";
import { VersionBadge } from "./VersionBadge";
import { useDrawer } from "../../utils/useDrawer";

const DRAWER_WIDTH_EXPANDED = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

// Permanent left-hand drawer for desktop. Collapsible to icon-only via
// the chevron on the outer edge. Logo at top, nav in the middle, theme
// toggle at the bottom.

export function DrawerDesktop() {
  const theme = useTheme();
  const { isOpen, toggle } = useDrawer();
  const collapsed = !isOpen;
  const width = isOpen ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED;

  return (
    <>
      <Drawer
        variant="permanent"
        open={isOpen}
        sx={{
          width,
          flexShrink: 0,
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          "& .MuiDrawer-paper": {
            width,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: isOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        <Stack flexGrow={1} ml={1} mr={0.5}>
          <Box
            component={RouterLink}
            to="/"
            ml={1}
            mt={1}
            mb={1.5}
            sx={{ textDecoration: "none", display: "block" }}
          >
            {collapsed ? (
              <LogoEye dark={theme.palette.mode === "dark"} width={32} height={32} />
            ) : (
              <Logo height={48} dark={theme.palette.mode === "dark"} />
            )}
          </Box>
          <DrawerNavMenu collapsed={collapsed} mr={0.5} flexGrow={1} />
          <Box mt={2} mb={1.5} mr={0.5}>
            <DrawerThemeToggle collapsed={collapsed} />
          </Box>
          <Box mb={1.5} mr={0.5} ml={1}>
            <VersionBadge collapsed={collapsed} />
          </Box>
        </Stack>
      </Drawer>

      <Box
        sx={{
          position: "fixed",
          left: width,
          top: "20px",
          transform: "translate(-50%, 0)",
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create("left", {
            easing: theme.transitions.easing.sharp,
            duration: isOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <IconButton
          onClick={toggle}
          aria-label="toggle drawer"
          sx={{
            width: 24,
            height: 24,
            boxShadow: 4,
            backgroundColor: "background.paper",
            border:
              theme.palette.mode === "light"
                ? undefined
                : `1px solid ${theme.palette.action.disabledBackground}`,
            "&:hover": { backgroundColor: "background.paper" },
          }}
        >
          {isOpen ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>
    </>
  );
}
